import {
  PLATFORM_FOOT_NOTE,
  PLATFORM_NAME,
  PRODUCTS,
  productName,
} from '@app/shared/brand'
import { Menu as MenuIcon } from 'lucide-react'
import { type ReactNode, useRef } from 'react'
import { currentProduct, siteOrigin } from '../lib/public-router'
import { Logo, type Mark } from './Logo'
import { Menu } from './Menu'
import { PoweredBy } from './PoweredBy'
import { SiteAccount } from './SiteAccount'
import { useFollowInPlace } from './useFollowInPlace'
import './site-shell.css'

/**
 * The PLATFORM's own site chrome — the masthead and footer the marketing pages
 * wear.
 *
 * Deliberately not `PublicShell`. That one is a CONGREGATION's site: its
 * masthead is the org's name and its nav is the pages that org publishes. The
 * two look alike on purpose and are answering different questions — who am I
 * looking at, and what can I read here — so folding them into one shell with a
 * mode flag would mean every future change to either had to state which site it
 * meant. They share the tokens, which is the part that must not drift.
 *
 * **Which of OUR sites it is wearing comes from the HOST**, because that is one
 * site with three brands rather than three sites, and the host is already what
 * decides which front door was served. A page naming its own product would be
 * a second answer to a question the address has settled, and the two can
 * disagree.
 *
 * The nav is passed rather than derived, since every item has to name something
 * the page it sits on actually has.
 */
/** A page of a site's own, named in its masthead. */
export type SiteLink = { label: string; href: string }

export function SiteShell({
  nav,
  onLogin,
  children,
}: {
  nav?: SiteLink[]
  onLogin?: () => void
  children: ReactNode
}) {
  const product = currentProduct()
  /*
   * The platform has a mark of its own, and borrowing a product's would say
   * that product runs the place.
   */
  const mark: Mark = product ?? 'platform'
  const name = product ? productName(product) : PLATFORM_NAME
  const note = product ? PRODUCTS[product].footNote : PLATFORM_FOOT_NOTE

  const chrome = useRef<HTMLDivElement>(null)
  useFollowInPlace(chrome)

  return (
    <div className="site" ref={chrome}>
      <header className="site__bar">
        <div className="site__measure site__bar-inner">
          <a className="site__brand" href="/">
            <Logo size={22} product={mark} />
            <span className="site__wordmark">{name}</span>
          </a>

          {/* Every item a site passes has to name something its page actually
              has. An anchor to a section that does not exist scrolls nowhere
              and says nothing went wrong, which is the one kind of broken a
              masthead cannot afford.

              Beside the brand where there is room; below that the whole group
              collapses to one entry point, whose items are the same links. */}
          {nav?.length ? (
            <>
              <nav className="site__nav" aria-label={name}>
                {nav.map((l) => (
                  <a key={l.href} className="site__link" href={l.href}>
                    {l.label}
                  </a>
                ))}
              </nav>
              <div className="site__navmenu">
                <Menu
                  items={nav}
                  ariaLabel={`${name} pages`}
                  icon={MenuIcon}
                  align="start"
                />
              </div>
            </>
          ) : null}

          <div className="site__account">
            <SiteAccount onSignIn={onLogin} />
          </div>
        </div>
      </header>

      <main className="site__body">{children}</main>

      <footer className="site__foot">
        <div className="site__measure site__foot-inner">
          <div className="site__foot-brand">
            <Logo size={20} product={mark} />
            <span className="site__wordmark">{name}</span>
          </div>
          <div className="site__foot-said">
            <p className="site__foot-note">{note}</p>
            {/* A product says who runs it; the platform's own site would be
                saying its name twice. */}
            {product ? (
              <PoweredBy name={PLATFORM_NAME} href={siteOrigin('platform')} />
            ) : null}
          </div>
        </div>
      </footer>
    </div>
  )
}
